/**
 * SelectEditCell - Edit cell for string values with predefined options
 *
 * Features:
 * - Auto-opens dropdown when cell enters edit mode
 * - Commits value immediately upon selection (no need to tab out)
 * - Supports both single and multiple selection
 * - Shows option colors if provided
 *
 * Usage:
 * - Status fields
 * - Type fields
 * - Vertical fields
 * - Any field with a fixed set of options
 */

import { Box, Checkbox, ListItemText, MenuItem, Select, Typography, useTheme } from '@mui/material'
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import React, { useCallback, useState } from 'react'
import { SelectOption } from './types'

interface SelectEditCellProps extends GridRenderEditCellParams {
  options: SelectOption[]
  multiple?: boolean
  getOptionColor?: (option: SelectOption) => string | undefined
  placeholder?: string
}

export const SelectEditCell: React.FC<SelectEditCellProps> = ({
  id,
  field,
  value,
  options,
  multiple = false,
  getOptionColor,
  placeholder = 'Select...',
}) => {
  console.log(id, field, value)
  const apiRef = useGridApiContext()
  const theme = useTheme()
  const [open, setOpen] = useState(true) // Auto-open dropdown immediately

  const handleOpen = useCallback(() => setOpen(true), [])

  const handleClose = useCallback(() => {
    setOpen(false)
    // Exit edit mode when closing dropdown
    apiRef.current.stopCellEditMode({ id, field })
  }, [apiRef, id, field])

  const handleChange = useCallback(
    (event: any) => {
      const newValue = event.target.value

      // Update the cell value
      apiRef.current.setEditCellValue({ id, field, value: newValue }, event)

      // For single select, immediately exit edit mode
      // For multiple select, keep open to allow more selections
      if (!multiple) {
        apiRef.current.stopCellEditMode({ id, field })
      }
    },
    [apiRef, id, field, multiple]
  )

  // For multiple select - commit when clicking away
  const handleMultipleClose = useCallback(() => {
    setOpen(false)
    apiRef.current.stopCellEditMode({ id, field })
  }, [apiRef, id, field])

  // Current value handling
  const currentValue = multiple ? (Array.isArray(value) ? value : value ? [value] : []) : value || ''

  // Render for multiple select
  if (multiple) {
    return (
      <Select
        autoFocus
        multiple
        open={open}
        onOpen={handleOpen}
        onClose={handleMultipleClose}
        value={currentValue}
        onChange={handleChange}
        fullWidth
        displayEmpty
        size="small"
        renderValue={selected => {
          if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            return <Typography color="text.secondary">{placeholder}</Typography>
          }
          const selectedArray = Array.isArray(selected) ? selected : [selected]
          const labels = selectedArray.map(val => options.find(opt => opt.value === val)?.label || val).join(', ')
          return <Typography noWrap>{labels}</Typography>
        }}
        sx={{
          bgcolor: 'transparent',
          '& .MuiSelect-select': {
            bgcolor: 'transparent',
            py: 0.5,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          PaperProps: {
            sx: {
              bgcolor: theme.palette.background.paper,
              maxHeight: 300,
              minWidth: 200,
            },
          },
        }}
      >
        {options.map(option => {
          const color = getOptionColor?.(option) || option.color
          const isSelected = currentValue.includes(option.value)
          return (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={isSelected} size="small" />
              <ListItemText primary={option.label} sx={{ color: color || 'inherit' }} />
            </MenuItem>
          )
        })}
      </Select>
    )
  }

  // Render for single select
  return (
    <Select
      autoFocus
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={currentValue}
      onChange={handleChange}
      fullWidth
      displayEmpty
      size="small"
      sx={{
        bgcolor: 'transparent',
        '& .MuiSelect-select': {
          bgcolor: 'transparent',
          py: 0.5,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      }}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        PaperProps: {
          sx: {
            bgcolor: theme.palette.background.paper,
            maxHeight: 300,
            minWidth: 180,
          },
        },
      }}
    >
      {!value && (
        <MenuItem value="" disabled>
          <Typography color="text.secondary">{placeholder}</Typography>
        </MenuItem>
      )}
      {options.map(option => {
        const color = getOptionColor?.(option) || option.color
        return (
          <MenuItem key={option.value} value={option.value}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {color && (
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography sx={{ color: color || 'inherit' }}>{option.label}</Typography>
            </Box>
          </MenuItem>
        )
      })}
    </Select>
  )
}

export default SelectEditCell
